import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { setMetricsObject, track } from '../../app/metrics';
import type { Session } from '../../api/session';
import { getObjects, type ObjectInfo } from '../../api/tenant';
import { chooseObject } from '../../domain/objects';
import type { TelegramApi } from '../../telegram/webapp';
import { LoadFailed } from '../common/LoadFailed';
import { SplashScreen } from '../auth/screens';
import { NoObjectsScreen } from './NoObjectsScreen';
import { DeepLinkRedirect } from '../../app/DeepLinkRedirect';
import s from './objects.module.css';

const STORAGE_KEY = 'objectId';

interface Props {
  session: Session;
  telegram: TelegramApi;
  children: (object: ObjectInfo, objects: ObjectInfo[], switchTo: (id: number) => void) => ReactNode;
  /** Object from a посёлок link `startapp=obj_<id>` (roadmap P1-6): open it, or offer to join it. */
  linkedObjectId?: number;
}

/** After login: no objects / choose / selected (spec §5.3). The choice lives in Telegram CloudStorage. */
export function ObjectGate({ session, telegram, children, linkedObjectId }: Props) {
  const objects = useQuery({ queryKey: ['objects'], queryFn: () => getObjects(session) });
  const [selectedId, setSelectedId] = useState<number | null | undefined>(undefined);

  useEffect(() => {
    if (!objects.data || selectedId !== undefined) return;
    let cancelled = false;
    void telegram.storage.get(STORAGE_KEY).then((saved) => {
      if (cancelled) return;
      const linked = objects.data.some((o) => o.id === linkedObjectId) ? String(linkedObjectId) : null;
      const choice = chooseObject(objects.data, linked ?? saved);
      setSelectedId(choice.kind === 'selected' ? choice.objectId : null);
    });
    return () => {
      cancelled = true;
    };
  }, [objects.data, selectedId, telegram]);

  // Once per launch: how many objects the resident has (0 — needs a join request) — usage metrics.
  const counted = useRef(false);
  useEffect(() => {
    if (!objects.data || counted.current) return;
    counted.current = true;
    const n = objects.data.length;
    track('objects', { k: n > 1 ? '2+' : n });
  }, [objects.data]);
  useEffect(() => {
    setMetricsObject(selectedId ?? null);
    const name = objects.data?.find((x) => x.id === selectedId)?.name;
    // The посёлок name once per choice, so the metrics page shows names, not ids.
    if (name) track('object', { on: name.slice(0, 40) });
  }, [selectedId, objects.data]);

  const select = (id: number) => {
    setSelectedId(id);
    void telegram.storage.set(STORAGE_KEY, String(id));
  };

  if (objects.isError) return <LoadFailed error={objects.error} onRetry={() => objects.refetch()} fullScreen />;
  if (!objects.data || selectedId === undefined) return <SplashScreen />;

  if (objects.data.length === 0) {
    return <NoObjectsScreen session={session} telegram={telegram} onRecheck={() => void objects.refetch()} joinObjectId={linkedObjectId} />;
  }

  const current = objects.data.find((o) => o.id === selectedId);
  if (!current) {
    return (
      <main className={s.picker}>
        <h1 className={s.title}>Выберите посёлок</h1>
        <p className={s.lead}>Ваш номер есть в нескольких объектах. Переключиться можно в любой момент — на главной.</p>
        <div className={s.list}>
          {objects.data.map((o) => (
            <button key={o.id} type="button" className={s.item} onClick={() => select(o.id)}>
              <span className={s.itemName}>{o.name}</span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                <path d="M9 6l6 6-6 6" />
              </svg>
            </button>
          ))}
        </div>
      </main>
    );
  }

  const foreign = linkedObjectId !== undefined && !objects.data.some((o) => o.id === linkedObjectId);
  return (
    <>
      {children(current, objects.data, select)}
      {foreign && <DeepLinkRedirect route={`/more/join?object=${linkedObjectId}`} />}
    </>
  );
}
